import { useContext } from "react";
import { UserContext } from "../../../../App";
import {
  newCartItemsAddingProducts,
  addToCartDedup,
} from "../../../../config/Services";
import {
  isStaleCartError,
  clearStaleCartStorage,
  showStaleCartDialog,
  isNoCartError,
  showNoCartDialog,
} from "../../../../utils/staleCartHandler";
import { useSnackbar } from "./SnackbarProvider";

/**
 * useAddToCart — shared add-to-cart hook.
 *
 * Centralises ALL error-handling for the two add-to-cart code paths so that
 * stale-cart, no-cart, no-inventory, and generic API errors are fixed in ONE
 * place rather than being duplicated across every product-listing component.
 *
 * Exposes:
 *
 *   addItemWithCheck(payload, opts)
 *     Wraps newCartItemsAddingProducts (/v1/cart/product/add/pos).
 *     opts.onSuccess(cartData)      – called after cart state is applied (direct add)
 *     opts.onBatchSelect(dataArr)   – called when multiple batches are returned
 *     opts.onOversell(rawRes)       – called when the product was inwarded but has no
 *                                    current stock (empty array, es===0). The raw axios
 *                                    response is passed so the caller can roll back any
 *                                    tentative add and show an oversell confirmation.
 *     opts.onBeforeError()          – optional; always fires before any error handling
 *                                    (use to clear loader state in the caller)
 *     opts.onError(errMsg)          – optional override for generic errors;
 *                                    defaults to showSnackbar
 *
 *   addItemDirect(payload, cartSnapshot, opts)
 *     Wraps addToCartDedup / AddItemThroughScanner (post-batch-selection path).
 *     opts.onSuccess(cartData)      – called after cart state is applied
 *     opts.onBeforeError()          – optional; always fires before any error handling
 *     opts.onError(errMsg)          – optional override; defaults to showSnackbar
 *
 *   applyCartState(data)
 *     Exposed so callers that receive a raw cart response from somewhere else
 *     (e.g. after editCart) can still apply the standard state update.
 */
export const useAddToCart = () => {
  const showSnackbar = useSnackbar();
  const {
    setCartItems,
    setCartId,
    setCgst,
    setSgst,
    setCess,
    setTotal,
    setSubTotal,
    setOfferFound,
    setCartTotalQty,
  } = useContext(UserContext);

  /**
   * Apply the standard cart-state update from a successful API cart object.
   * Accepts either a raw cartData object ({ cartProducts, billing, ... }) or
   * the res.data.data.data shape — it unwraps one level automatically.
   */
  const applyCartState = (data) => {
    if (!data) return;

    // Unwrap one extra level if callers pass res.data.data instead of res.data.data.data
    const cartData = data?.cartProducts ? data : data?.data?.cartProducts ? data.data : data;

    const products = cartData?.cartProducts;
    if (Array.isArray(products)) {
      const sorted = [...products].sort((a, b) =>
        (b.modifiedDate || b.createdDate || "").localeCompare(
          a.modifiedDate || a.createdDate || ""
        )
      );
      setCartItems(sorted);
    }
    if (cartData?.billing) {
      setCgst(cartData.billing.cgst);
      setSgst(cartData.billing.sgst);
      setCess(cartData.billing.totalCessAmount);
      setTotal(cartData.billing.totalCartValue);
      setSubTotal(cartData.billing.subtotal);
      if (typeof setCartTotalQty === "function") {
        setCartTotalQty(cartData.billing.quantity);
      }
    }
    if (cartData?.offerProductFound !== undefined) {
      setOfferFound(cartData.offerProductFound);
    }
  };

  /**
   * Normalize the batch-items array returned by newCartItemsAddingProducts
   * into the standard dataArr shape used by all batch-selection modals.
   */
  const normalizeBatchItems = (items) =>
    (items || []).map((item) => ({
      mrp: item?.mrp,
      sellingPrice: item?.sellingPrice,
      gtin: item?.gtin,
      batch: item?.batchNo,
      quantity: item?.availableUnits,
      batchNum: item?.batchNo,
      batchNo: item?.batchNo,
      offers: item?.offersList ? item : null,
      purchasePrice: item?.purchasePrice,
      isMultiple: item?.multipleBatches,
      availableUnits: item?.availableUnits,
      haveOffer: item?.haveOffer,
      variantName: item?.itemName,
      itemName: item?.itemName,
      vendorId: item?.vendorId,
      brand: item?.brand,
      sellingUnit: item?.sellingUnit,
      expiryDate: item?.expiryDate,
      packagingType: item?.packagingType,
    }));

  /**
   * addItemWithCheck — wraps newCartItemsAddingProducts.
   *
   * Response shapes handled:
   *   (A) Direct add:  res.data.data.data = { cartProducts, billing, ... }
   *   (B) Multi-batch: res.data.data.data = Array (length > 1)  or statusCode 1101
   *   (C) Single-item array: delegates to onBatchSelect so caller decides
   *   (D) Error:       res.data.data.es === 1 or 2
   *
   * @param {Object} payload                  API payload ({ gtin, cartId, locationId, orgId, … })
   * @param {Object} [opts]
   * @param {function} [opts.onSuccess]        Called with raw cartData after state applied (A)
   * @param {function} [opts.onBatchSelect]    Called with normalised dataArr (B/C)
   * @param {function} [opts.onBeforeError]    Always fires before any error dialog/snackbar —
   *                                           use to clear local loader state
   * @param {function} [opts.onError]          Generic error callback; default: showSnackbar
   * @returns {Promise<void>}
   */
  const addItemWithCheck = async (payload, opts = {}) => {
    const { onSuccess, onBatchSelect, onOversell, onBeforeError, onError } = opts;
    try {
      const res = await newCartItemsAddingProducts(payload);
      const esData = res?.data?.data;
      const es = esData?.es;
      const rawData = esData?.data;
      const statusCode = esData?.statusCode;

      // ── Error response ─────────────────────────────────────────────────────
      // statusCode 1101 means "Multiple Inventory Found" — es=1 is expected there,
      // it is NOT an error. Skip the error path so the batch handler below runs.
      if ((es === 1 || es === 2) && statusCode !== 1101) {
        const errMsg = esData?.message || "";
        if (typeof onBeforeError === "function") onBeforeError();
        if (isStaleCartError(errMsg)) {
          clearStaleCartStorage();
          setCartItems([]);
          if (typeof setCartId === "function") setCartId(null);
          showStaleCartDialog();
          return;
        }
        if (isNoCartError(errMsg)) {
          showNoCartDialog();
          return;
        }
        if (typeof onError === "function") {
          onError(errMsg, "api");
        } else {
          showSnackbar(errMsg, "error");
        }
        return;
      }

      // ── Multiple batches ────────────────────────────────────────────────────
      // statusCode 1101 also signals a multi-batch scenario in some API versions
      if (
        (Array.isArray(rawData) && rawData.length > 1 && es === 0) ||
        statusCode == 1101
      ) {
        const dataArr = normalizeBatchItems(Array.isArray(rawData) ? rawData : []);
        if (typeof onBatchSelect === "function") {
          onBatchSelect(dataArr);
        }
        return;
      }

      // ── Single batch – caller decides (auto-add or picker) ──────────────────
      if (Array.isArray(rawData) && rawData.length === 1) {
        const dataArr = normalizeBatchItems(rawData);
        if (typeof onBatchSelect === "function") {
          onBatchSelect(dataArr);
        }
        return;
      }

      // ── Empty array (es===0): inwarded product but no current stock ──────────
      // The endpoint may have tentatively placed the item in the cart already.
      // Pass the raw response so the caller can roll back and show an oversell
      // confirmation dialog before committing the add.
      if (Array.isArray(rawData) && rawData.length === 0) {
        if (typeof onOversell === "function") {
          await onOversell(res);
        }
        return;
      }

      // ── Direct add – cart already updated ──────────────────────────────────
      if (rawData && typeof rawData === "object" && rawData.cartProducts) {
        applyCartState(rawData);
        if (typeof onSuccess === "function") onSuccess(rawData);
        return;
      }

      // ── Fallback: unknown shape ──────────────────────────────────────────────
      const fallback = rawData || esData;
      if (fallback?.cartProducts) {
        applyCartState(fallback);
        if (typeof onSuccess === "function") onSuccess(fallback);
      }
    } catch (err) {
      const errMsg = err?.response?.data?.message || err?.message || "";
      if (typeof onBeforeError === "function") onBeforeError();
      if (isStaleCartError(errMsg)) {
        clearStaleCartStorage();
        setCartItems([]);
        if (typeof setCartId === "function") setCartId(null);
        showStaleCartDialog();
        return;
      }
      if (isNoCartError(errMsg)) {
        showNoCartDialog();
        return;
      }
      if (typeof onError === "function") {
        onError(errMsg, "network");
      } else {
        showSnackbar(errMsg, "error");
      }
    }
  };

  /**
   * addItemDirect — wraps addToCartDedup / AddItemThroughScanner.
   * Used after batch selection to perform the actual cart add.
   *
   * @param {Object} payload                API payload for addToCartDedup
   * @param {Array}  [cartSnapshot=[]]      Current cartItems (for dedup logic)
   * @param {Object} [opts]
   * @param {function} [opts.onSuccess]     Called with raw cartData after state applied
   * @param {function} [opts.onBeforeError] Always fires before any error handling
   * @param {function} [opts.onError]       Generic error callback; default: showSnackbar
   * @returns {Promise<void>}
   */
  const addItemDirect = async (payload, cartSnapshot = [], opts = {}) => {
    const { onSuccess, onBeforeError, onError } = opts;
    try {
      const res = await addToCartDedup(payload, cartSnapshot);
      const esData = res?.data?.data;
      const es = esData?.es;

      if (es === 1 || es === 2) {
        const errMsg = esData?.message || "";
        if (typeof onBeforeError === "function") onBeforeError();
        if (isStaleCartError(errMsg)) {
          clearStaleCartStorage();
          setCartItems([]);
          if (typeof setCartId === "function") setCartId(null);
          showStaleCartDialog();
          return;
        }
        if (isNoCartError(errMsg)) {
          showNoCartDialog();
          return;
        }
        if (typeof onError === "function") {
          onError(errMsg, "api");
        } else {
          showSnackbar(errMsg, "error");
        }
        return;
      }

      const cartData = esData?.data || esData;
      if (cartData) {
        applyCartState(cartData);
        if (typeof onSuccess === "function") onSuccess(cartData);
      }
    } catch (err) {
      const errMsg = err?.response?.data?.message || err?.message || "";
      if (typeof onBeforeError === "function") onBeforeError();
      if (isStaleCartError(errMsg)) {
        clearStaleCartStorage();
        setCartItems([]);
        if (typeof setCartId === "function") setCartId(null);
        showStaleCartDialog();
        return;
      }
      if (isNoCartError(errMsg)) {
        showNoCartDialog();
        return;
      }
      if (typeof onError === "function") {
        onError(errMsg, "network");
      } else {
        showSnackbar(errMsg, "error");
      }
    }
  };

  return { addItemWithCheck, addItemDirect, applyCartState };
};
